// SocialOS SDK - Types & Interfaces
// This file re-exports all types from the SDK

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  message?: string;
}

export interface ApiError {
  code: string;
  message: string;
  status?: number;
  details?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

// === Auth Types ===

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  verified: boolean;
  created_at: string;
  updated_at: string;
  last_login: string | null;
  // Extended properties used in the app
  display_name?: string;
  full_name?: string;
  user_metadata?: Record<string, unknown>;
}

export interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  bio: string | null;
  location: string | null;
  avatar_url: string | null;
  energy: number;
  xp_points: number;
  level: number;
  created_at: string;
  updated_at: string;
}

export interface Session {
  access_token: string;
  user: User;
  expires_at: number;
  refresh_token?: string;
}

export interface AuthResponse {
  user: User;
  session: Session;
}

export interface SignUpCredentials {
  email: string;
  password: string;
  name?: string;
}

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface UpdateProfileData {
  name?: string;
  avatar_url?: string;
  bio?: string;
}

// === WebSocket Types ===

export enum WebSocketEventType {
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  RECONNECT = 'reconnect',
  MESSAGE = 'message',
  TYPING = 'typing',
  MESSAGE_READ = 'message-read',
  MESSAGE_REACTION = 'message-reaction',
  PRESENCE = 'presence',
  FOLLOW = 'follow',
  POST_CREATE = 'post-create',
  POST_UPDATE = 'post-update',
  POST_DELETE = 'post-delete'
}

export interface WebSocketMessage<T = unknown> {
  event: WebSocketEventType | string;
  payload: T;
  timestamp: number;
  connectionId?: string;
}

export interface WebSocketConnectionState {
  connected: boolean;
  connecting: boolean;
  reconnecting: boolean;
  lastConnected: number | null;
  reconnectAttempts: number;
  error: Error | null;
}

// === Query Types ===

export interface QueryOptions {
  select?: string | string[];
  filter?: Record<string, unknown>;
  orderBy?: string | { column: string; direction: 'asc' | 'desc' };
  limit?: number;
  offset?: number;
  single?: boolean;
}

export interface InsertOptions {
  returning?: boolean;
}

export interface UpdateOptions {
  returning?: boolean;
}

// === Supabase Proxy Type ===

export interface SupabaseProxy {
  auth: {
    signUp(credentials: SignUpCredentials): Promise<AuthResponse>;
    signInWithPassword(credentials: SignInCredentials): Promise<AuthResponse>;
    getSession(): Promise<{ data: { session: Session | null } }>;
    updateUser(attributes: UpdateProfileData): Promise<{ user: User | null }>;
    signOut(): Promise<void>;
    resetPasswordForEmail(email: string): Promise<void>;
    getUser(): Promise<{ data: { user: User | null } }>;
    updateUser(attributes: UpdateProfileData): Promise<{ data: { user: User | null } }>;
  };
  from(table: string): {
    select(columns?: string): SupabaseQueryBuilder;
    insert(values: Record<string, unknown>): Promise<{ data: unknown; error: unknown }>;
    update(values: Record<string, unknown>): SupabaseQueryBuilder;
    delete(): SupabaseQueryBuilder;
    upsert(values: Record<string, unknown>): Promise<{ data: unknown; error: unknown }>;
  };
  rpc(fn: string, params?: Record<string, unknown>): Promise<unknown>;
  functions: {
    invoke(name: string, opts?: Record<string, unknown>): Promise<{ data: unknown; error: unknown }>;
  };
  storage: {
    from(bucket: string): {
      upload(path: string, file: File): Promise<{ data: unknown; error: unknown }>;
    };
  };
}

// === Supabase Proxy Types ===

export interface SupabaseQueryBuilder {
  select(columns?: string): SupabaseQueryBuilder;
  eq(column: string, value: unknown): SupabaseQueryBuilder;
  in(column: string, values: unknown[]): SupabaseQueryBuilder;
  order(column: string, opts?: { ascending?: boolean }): SupabaseQueryBuilder;
  limit(count: number): SupabaseQueryBuilder;
  single(): Promise<{ data: unknown; error: unknown }>;
  then(resolve: (value: { data: unknown; error: unknown }) => void): Promise<{ data: unknown; error: unknown }>;
}

// === Other Types ===

export interface OfflineOperation {
  id: string;
  type: 'insert' | 'update' | 'delete';
  table: string;
  data: unknown;
  timestamp: number;
  retries: number;
  maxRetries: number;
  resolve?: (value: unknown) => void;
  reject?: (error: unknown) => void;
}

export interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableStatuses: number[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  retryableStatuses: [408, 429, 500, 502, 503, 504]
};

export interface EventListener {
  event: string;
  callback: (data: unknown) => void;
}

export interface StorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}
